import { useMemo } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { type ColumnDef, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { Send, Users, UserX } from "lucide-react";
import { useTranslation } from "react-i18next";

import { adminApi } from "@/api/endpoints";
import {
  ActiveFilters,
  ColumnVisibility,
  DataTable,
  TablePagination,
} from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { PageHeader } from "@/components/shared/page-header";
import { RememberParams } from "@/components/shared/remember-params";
import { UserAvatar } from "@/components/shared/user-avatar";
import { currentLanguage } from "@/configs/i18n";
import { queryKeys } from "@/configs/query-keys";
import { useColumnVisibility } from "@/hooks/use-column-visibility";
import { type FilterBinding, useUrlColumnFilters } from "@/hooks/use-url-column-filters";
import { useUrlPagination } from "@/hooks/use-url-pagination";
import { daysSince, displayName, formatDateTime, formatNumber, percent } from "@/lib/format";
import { readEnum } from "@/lib/url-params";
import { cn } from "@/lib/utils";
import type { AdminUser } from "@/types/api";

const STATUSES = ["active", "blocked"] as const;
const ACTIVITY = ["active", "inactive"] as const;

const BINDINGS: FilterBinding[] = [
  { columnId: "name", param: "q", replace: true },
  { columnId: "status", param: "status", allowed: STATUSES },
  { columnId: "lastActive", param: "activity", allowed: ACTIVITY },
];

/** Qaytib kelinganda tiklanadigan tanlov — sahifa raqamisiz. */
const REMEMBERED = ["q", "status", "activity", "limit"] as const;

export function UsersPage() {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader title={t("users.title")} subtitle={t("users.subtitle")} icon={Users} tone="sky" />
      <RememberParams table="users" keys={REMEMBERED}>
        <UsersTable />
      </RememberParams>
    </>
  );
}

function UsersTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { page, limit, setPage, setLimit } = useUrlPagination();
  const { columnFilters, onColumnFiltersChange } = useUrlColumnFilters(BINDINGS);
  // Til va vaqt zonasi — ikkinchi darajali: standartda yashirin.
  const { columnVisibility, onColumnVisibilityChange } = useColumnVisibility("users", {
    language: false,
    createdAt: false,
    lastLogin: false,
  });

  const status = readEnum(params, "status", STATUSES);
  const search = params.get("q")?.trim() || undefined;
  const filters = {
    page,
    limit,
    search,
    blocked: status === undefined ? undefined : status === "blocked",
    activity: readEnum(params, "activity", ACTIVITY),
  };
  const users = useQuery({
    queryKey: queryKeys.users(filters),
    queryFn: () => adminApi.users(filters),
    placeholderData: keepPreviousData,
  });

  const columns = useUserColumns();
  const table = useReactTable({
    data: users.data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id,
    state: {
      columnFilters,
      columnVisibility,
      pagination: { pageIndex: page - 1, pageSize: limit },
    },
    onColumnFiltersChange,
    onColumnVisibilityChange,
    // Filtr va sahifalash serverda: bir sahifani filtrlash yolg'on natija berardi.
    manualFiltering: true,
    manualPagination: true,
    enableSorting: false,
    pageCount: users.data?.meta.totalPages ?? 0,
  });

  const filtered = columnFilters.length > 0;

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ActiveFilters table={table} />
        <div className="ml-auto">
          <ColumnVisibility table={table} />
        </div>
      </div>
      <DataTable
        table={table}
        isLoading={users.isPending}
        isFetching={users.isFetching}
        error={users.error}
        onRetry={() => void users.refetch()}
        onRowClick={row => navigate(`/users/${row.original.id}`)}
        rowLabel={row => t("users.open", { name: displayName(row.original) })}
        empty={
          <EmptyState
            icon={UserX}
            title={t("users.emptyTitle")}
            text={filtered ? t("users.emptyText") : t("users.emptyNoUsers")}
          />
        }
      />
      {users.data && users.data.meta.total > 0 ? (
        <TablePagination
          page={page}
          limit={limit}
          total={users.data.meta.total}
          totalPages={users.data.meta.totalPages}
          onPage={setPage}
          onLimit={setLimit}
        />
      ) : null}
    </section>
  );
}

function useUserColumns(): ColumnDef<AdminUser>[] {
  const { t } = useTranslation();
  const lang = currentLanguage();

  return useMemo<ColumnDef<AdminUser>[]>(
    () => [
      {
        id: "name",
        accessorFn: row => displayName(row),
        header: t("users.name"),
        enableHiding: false,
        meta: { filterVariant: "text", label: t("users.name"), width: 280 },
        cell: ({ row }) => {
          const user = row.original;
          const name = displayName(user);
          return (
            <div className="flex min-w-0 items-center gap-3">
              <UserAvatar name={name} seed={user.id} />
              <div className="min-w-0">
                <p className="truncate font-medium">{name}</p>
                <p className="text-muted-foreground truncate text-xs">
                  {user.telegramUsername ? `@${user.telegramUsername} · ` : ""}
                  <span className="font-mono">{user.telegramUserId}</span>
                </p>
              </div>
            </div>
          );
        },
      },
      {
        id: "tasks",
        accessorKey: "taskCount",
        header: t("users.tasks"),
        meta: { label: t("users.tasks"), width: 150 },
        cell: ({ row }) => {
          const { completedTaskCount: done, taskCount: total } = row.original;
          const rate = percent(done, total);
          return (
            <div className="space-y-1">
              <p className="text-xs tabular-nums">
                <span className="font-semibold">{done}</span>
                <span className="text-muted-foreground">/{total}</span>
              </p>
              <div className="bg-muted h-1.5 w-24 overflow-hidden rounded-full">
                <div
                  className={cn(
                    "h-full rounded-full transition-[width] duration-700",
                    rate >= 60 ? "bg-success" : rate >= 30 ? "bg-warning" : "bg-primary",
                  )}
                  style={{ width: `${rate}%` }}
                />
              </div>
            </div>
          );
        },
      },
      {
        id: "goals",
        accessorKey: "goalCount",
        header: t("users.goals"),
        meta: { label: t("users.goals"), width: 110, align: "end" },
        cell: ({ row }) => (
          <span className="font-medium tabular-nums">{formatNumber(row.original.goalCount)}</span>
        ),
      },
      {
        id: "telegram",
        accessorKey: "telegramCanMessage",
        header: t("users.telegram"),
        meta: { label: t("users.telegram"), width: 180 },
        cell: ({ row }) => {
          const ok = row.original.telegramCanMessage;
          return (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 text-xs font-medium",
                ok ? "text-success" : "text-destructive",
              )}
            >
              <Send className="size-3.5" aria-hidden />
              {ok ? t("users.reachable") : t("users.unreachable")}
            </span>
          );
        },
      },
      {
        id: "language",
        accessorKey: "language",
        header: t("users.language"),
        meta: { label: t("users.language"), width: 110 },
        cell: ({ row }) => (
          <span className="bg-muted rounded-md px-1.5 py-0.5 font-mono text-xs uppercase">
            {row.original.language}
          </span>
        ),
      },
      {
        id: "lastActive",
        accessorFn: row => row.lastActiveAt,
        header: t("users.lastActive"),
        meta: {
          label: t("users.lastActive"),
          width: 200,
          filterVariant: "select",
          filterOptions: [
            { value: "active", label: t("users.activityActive") },
            { value: "inactive", label: t("users.activityInactive") },
          ],
        },
        cell: ({ row }) => <LastActive at={row.original.lastActiveAt} />,
      },
      {
        id: "lastLogin",
        accessorKey: "lastLoginAt",
        header: t("users.lastLogin"),
        meta: { label: t("users.lastLogin"), width: 200 },
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs whitespace-nowrap">
            {formatDateTime(row.original.lastLoginAt, lang)}
          </span>
        ),
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: t("users.joined"),
        meta: { label: t("users.joined"), width: 200 },
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs whitespace-nowrap">
            {formatDateTime(row.original.createdAt, lang)}
          </span>
        ),
      },
      {
        id: "status",
        accessorFn: row => (row.isBlocked ? "blocked" : "active"),
        header: t("users.status"),
        meta: {
          label: t("users.status"),
          width: 190,
          filterVariant: "select",
          filterOptions: [
            { value: "active", label: t("users.active") },
            { value: "blocked", label: t("users.blocked") },
          ],
        },
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            <StatusPill blocked={row.original.isBlocked} />
            {row.original.isAdmin ? (
              <span className="bg-tile-violet text-tile-violet-strong inline-flex h-6 items-center rounded-full px-2 text-xs font-medium">
                {t("users.admin")}
              </span>
            ) : null}
          </div>
        ),
      },
    ],
    [t, lang],
  );
}

/** Oxirgi amal — nisbiy vaqt va rang: 7 kun ichida — yashil (aktiv), keyin — kulrang. */
function LastActive({ at }: { at: string | null }) {
  const { t } = useTranslation();
  const lang = currentLanguage();
  if (!at) return <span className="text-muted-foreground text-xs">{t("users.neverActive")}</span>;
  const days = daysSince(at);
  const active = days < 7;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap"
      title={formatDateTime(at, lang)}
    >
      <span
        className={cn("size-1.5 rounded-full", active ? "bg-success" : "bg-muted-foreground/40")}
      />
      <span className={active ? "text-foreground" : "text-muted-foreground"}>
        {days <= 0 ? t("users.activeToday") : t("users.daysAgo", { count: days })}
      </span>
    </span>
  );
}

function StatusPill({ blocked }: { blocked: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        "inline-flex h-6 items-center gap-1.5 rounded-full px-2 text-xs font-medium",
        blocked ? "bg-destructive/10 text-destructive" : "bg-success-soft text-success",
      )}
    >
      <span
        className={cn(
          "size-1.5 rounded-full",
          blocked ? "bg-destructive" : "bg-success animate-pulse-ring text-success",
        )}
      />
      {blocked ? t("users.blocked") : t("users.active")}
    </span>
  );
}
